import {useCallback, useMemo, useRef} from 'react';
import {getTimeProgress, getTimeStr} from '../../../common/shared/utils/time';
import {twMerge} from '../../../common/twMerge';
import {VideoStatus} from '../../../domain/content/[seriesId]/viewer/[productId]/constant/videoViewerConstant';
import {ViewerType} from '../../../domain/content/[seriesId]/viewer/[productId]/constant/viewerConstant';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {MobileViewerSeekbar} from '../MobileViewerSeekbar/MobileViewerSeekbar';
import {PcVideoViewerSeekbar} from '../PcVideoViewerSeekbar/PcVideoViewerSeekbar';
import {VideoPlayerController} from '../VideoPlayerController/VideoPlayerController';
import {VideoPlayerControlBoxProps} from './VideoPlayerControlBoxProps.interface';

export default function VideoPlayerControlBox({
  videoStatus,
  currentTime,
  loadedProgress,
  duration,
  isLoading,
  isMute,
  isMobile,
  volume,
  className,
  isShortsDrama,
  isSeeking,
  hasNextEpisode,
  onClickMuteBtn,
  onClickPlayBtn,
  onClickPauseBtn,
  onClickReplayBtn,
  onClickFullscreenBtn,
  onClickPrevMoveBtn,
  onClickNextMoveBtn,
  onClickTogglePlayback,
  onChangeProgress,
  onPressProgressBar,
  onReleaseProgressBar,
  onChangeVolume,
  ticketFlowInProgress,
  isEpisodeChanging,
  ...props
}: VideoPlayerControlBoxProps) {
  const VolumeRef = useRef<HTMLDivElement>(null);

  // 현재 재생 프로그래스 (0~1)
  const currentProgress = getTimeProgress(currentTime, duration);
  // 현재 재생 시간 (시간 : 분 : 초)
  const currentTimeText = getTimeStr(currentTime, isMobile && isShortsDrama);
  // 총 영상 길이 (시간 : 분 : 초)
  const totalTimeText = useMemo(() => getTimeStr(duration), [duration]);

  const isShowTimeMoveBtn = isLoading || videoStatus !== VideoStatus.Ended;

  const isVideoVisible =
    videoStatus === VideoStatus.Playing ||
    (videoStatus === VideoStatus.BeforeInitialPlay && isMute) ||
    ticketFlowInProgress ||
    isEpisodeChanging;

  const handleScreenAndPlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (isShortsDrama) {
        if (VolumeRef.current && VolumeRef.current.contains(e.target as Node)) {
          return;
        }
        onClickTogglePlayback(e);
        return;
      }
    },
    [isShortsDrama, onClickTogglePlayback]
  );

  return (
    <div
      className={twMerge(
        'absolute top-0 left-0 h-full w-full',
        isMobile && !isShortsDrama && 'bg-static-etc-dimmed',
        isMobile && isShortsDrama && !isVideoVisible && 'bg-static-etc-dimmed',
        className
      )}
      onMouseDown={isShortsDrama ? handleScreenAndPlayClick : undefined}
      {...props}
    >
      <VideoPlayerController
        isShortsDrama={isShortsDrama}
        isVideoVisible={isVideoVisible}
        isMobile={isMobile}
        isShowTimeMoveBtn={isShowTimeMoveBtn}
        isLoading={isLoading}
        videoStatus={videoStatus}
        onClickPrevMoveBtn={onClickPrevMoveBtn}
        onClickNextMoveBtn={onClickNextMoveBtn}
        onClickPlayBtn={isShortsDrama ? undefined : onClickPlayBtn}
        onClickReplayBtn={isShortsDrama ? undefined : onClickReplayBtn}
        onClickPauseBtn={isShortsDrama ? undefined : onClickPauseBtn}
      />
      {
        //PC버전 하단 그라데이션
        !isMobile && (
          <div
            className="absolute bottom-0 h-96pxr w-full"
            style={{
              background:
                'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)',
            }}
          />
        )
      }
      {isMobile ? (
        <MobileViewerSeekbar
          isShortsDrama={isShortsDrama}
          isSeeking={isSeeking}
          isVideoVisible={isVideoVisible}
          viewerType={ViewerType.VideoViewer}
          currentProgress={currentProgress}
          currentTime={currentTimeText}
          loadedProgress={loadedProgress}
          totalTime={totalTimeText}
          onChangeProgress={onChangeProgress}
          onPressProgressBar={onPressProgressBar}
          onReleaseProgressBar={onReleaseProgressBar}
          onClickFullscreenBtn={onClickFullscreenBtn}
          className="absolute bottom-12pxr left-0"
        />
      ) : (
        <PcVideoViewerSeekbar
          ref={VolumeRef}
          isShortsDrama={isShortsDrama}
          currentProgress={currentProgress}
          currentTime={currentTimeText}
          loadedProgress={loadedProgress}
          totalTime={totalTimeText}
          isMute={isMute}
          volume={volume}
          onChangeProgress={onChangeProgress}
          onPressProgressBar={onPressProgressBar}
          onReleaseProgressBar={onReleaseProgressBar}
          onClickMute={onClickMuteBtn}
          onClickFullscreenBtn={onClickFullscreenBtn}
          onChangeVolume={onChangeVolume}
        />
      )}
      {isMobile && (
        <div
          ref={VolumeRef}
          className={twMerge(
            'absolute top-12pxr right-10pxr ml-12pxr flex h-28pxr w-28pxr cursor-pointer items-center justify-center rounded-full bg-static-mono-90',
            isShortsDrama && isVideoVisible && 'hidden'
          )}
          {...putTiaraObjectProps({
            click: {
              layer2: isShortsDrama ? '헤더' : '기타',
              copy: isMute ? 'on' : 'off',
            },
          })}
        >
          <Icon
            type={
              isMute ? IconType.PlayerMuteStatic : IconType.PlayerSoundStatic
            }
            onClick={onClickMuteBtn}
          />
        </div>
      )}
    </div>
  );
}
